import { z } from 'zod';
import {
  idParamSchema,
  nameAndCode,
  optionalEmail,
  optionalPhone,
  optionalString,
  paginationQuerySchema,
  queryBoolean,
  requiredString,
} from './common';
import { lotStatusSchema, stockMovementKindSchema, stockUnitSchema } from './enums';

/**
 * Reagent inventory inputs.
 *
 * Every quantity is in the item's own base unit. See `availability.ts` for the
 * one conversion — there is no unit-conversion engine, deliberately.
 */

/**
 * A quantity in base units.
 *
 * Four decimals, matching `DECIMAL(14,4)`: 12.5 µL pipetting steps and 2.5 g
 * weighings are real, and `floor(x / 0.5)` for a half-strip consumable needs at
 * least one. Two would be too few; six would be theatre.
 */
const quantityBase = z.coerce.number().min(0).max(9_999_999_999).multipleOf(0.0001);
const positiveQuantity = quantityBase.refine((value) => value > 0, 'Must be more than zero');

/* ------------------------------------------------------------------------- *
 * Suppliers
 * ------------------------------------------------------------------------- */

export const createSupplierSchema = z.object({
  ...nameAndCode,
  contact_name: optionalString(),
  phone_number: optionalPhone(),
  email: optionalEmail(),
  address: optionalString(),
  note: optionalString(),
  is_active: z.boolean().default(true),
});
export type CreateSupplierInput = z.infer<typeof createSupplierSchema>;
export const updateSupplierSchema = createSupplierSchema;
export type UpdateSupplierInput = z.infer<typeof updateSupplierSchema>;

/* ------------------------------------------------------------------------- *
 * Items
 * ------------------------------------------------------------------------- */

export const createReagentItemSchema = z.object({
  ...nameAndCode,
  barcode: optionalString(),
  department_id: idParamSchema,
  default_supplier_id: idParamSchema.nullish(),
  base_unit: stockUnitSchema,
  /** For the UI only: "1 × 50 mL bottle", "100 tests/kit". */
  pack_label: requiredString('Pack description'),
  /**
   * Base units in one pack.
   *
   * Immutable once the item has any movement — the service refuses it, because
   * changing it retroactively rewrites what every past receipt meant. Lots
   * snapshot it for the same reason.
   */
  pack_size_base: positiveQuantity,
  reorder_level_base: quantityBase.default(0),
  storage_location: optionalString(),
  note: optionalString(),
  is_active: z.boolean().default(true),
});
export type CreateReagentItemInput = z.infer<typeof createReagentItemSchema>;
export const updateReagentItemSchema = createReagentItemSchema;
export type UpdateReagentItemInput = z.infer<typeof updateReagentItemSchema>;

export const reagentItemQuerySchema = paginationQuerySchema.extend({
  department_id: idParamSchema.optional(),
  /** Only items at or below their reorder level. */
  low_stock: queryBoolean.optional(),
  active: z.enum(['true', 'false']).optional(),
});
export type ReagentItemQuery = z.infer<typeof reagentItemQuerySchema>;

/* ------------------------------------------------------------------------- *
 * Lots
 * ------------------------------------------------------------------------- */

/**
 * Entering stock the lab already has, or a delivery received outside a
 * purchase order.
 *
 * Posts an `opening_balance` movement, so even day-one counts arrive through
 * the ledger and reconciliation holds from the very first row.
 */
export const createReagentLotSchema = z.object({
  item_id: idParamSchema,
  /** `—` is a legitimate value for a consumable with no vendor batch. */
  lot_no: requiredString('Lot number'),
  expiry_at: z.iso.date().nullish(),
  /** Whole packs, because that is what arrives. Converted to base units on save. */
  quantity_packs: z.coerce.number().int().min(1).max(100_000),
  /**
   * Price of one *pack*, not one base unit.
   *
   * Money as it appears on an invoice; the per-base-unit cost is derived. Asking
   * a receiving clerk for "the cost per microlitre" is how a zero gets typed.
   */
  pack_price: z.coerce.number().min(0).max(99_999_999).multipleOf(0.01).default(0),
  /** Overrides the item's pack size when the vendor shipped a different one. */
  pack_size_base: positiveQuantity.optional(),
  supplier_id: idParamSchema.nullish(),
  note: optionalString(),
});
export type CreateReagentLotInput = z.infer<typeof createReagentLotSchema>;

export const updateLotStatusSchema = z.object({
  status: lotStatusSchema,
  reason: optionalString(),
});
export type UpdateLotStatusInput = z.infer<typeof updateLotStatusSchema>;

export const reagentLotQuerySchema = paginationQuerySchema.extend({
  item_id: idParamSchema.optional(),
  status: lotStatusSchema.optional(),
  /** Lots expiring within this many days. `0` means already expired. */
  expiring_days: z.coerce.number().int().min(0).max(3650).optional(),
});
export type ReagentLotQuery = z.infer<typeof reagentLotQuerySchema>;

/* ------------------------------------------------------------------------- *
 * Movements
 * ------------------------------------------------------------------------- */

export const stockMovementQuerySchema = paginationQuerySchema.extend({
  item_id: idParamSchema.optional(),
  lot_id: idParamSchema.optional(),
  kind: stockMovementKindSchema.optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
export type StockMovementQuery = z.infer<typeof stockMovementQuerySchema>;

/**
 * A correction, in either direction.
 *
 * `quantity_base` is signed here, unlike everywhere else: a stock count can come
 * out higher as well as lower, and forcing the caller to pick a "kind" to encode
 * the sign would mean two endpoints for one action.
 */
export const createAdjustmentSchema = z.object({
  lot_id: idParamSchema,
  quantity_base: z.coerce
    .number()
    .multipleOf(0.0001)
    .refine((value) => value !== 0, 'An adjustment of zero has no meaning'),
  reason: requiredString('Reason'),
});
export type CreateAdjustmentInput = z.infer<typeof createAdjustmentSchema>;

/** Stock thrown away: a dropped vial, a failed run, an expired lot discarded. */
export const createWastageSchema = z.object({
  lot_id: idParamSchema,
  /** Positive. The service negates it — asking for "-50" invites a sign error. */
  quantity_base: positiveQuantity,
  reason: requiredString('Reason'),
});
export type CreateWastageInput = z.infer<typeof createWastageSchema>;

/* ------------------------------------------------------------------------- *
 * Recipes
 * ------------------------------------------------------------------------- */

export const createReagentRecipeSchema = z
  .object({
    item_id: idParamSchema,
    test_id: idParamSchema.nullish(),
    parameter_id: idParamSchema.nullish(),
    quantity_base: positiveQuantity,
    note: optionalString(),
    is_active: z.boolean().default(true),
  })
  // Mirrors the `num_nonnulls(...) = 1` CHECK, so the operator gets a field
  // error rather than a 500 from a constraint violation.
  .refine(
    (value) => (value.test_id == null) !== (value.parameter_id == null),
    'Attach the recipe to a lab test or to a parameter, not both',
  );
export type CreateReagentRecipeInput = z.infer<typeof createReagentRecipeSchema>;
export const updateReagentRecipeSchema = createReagentRecipeSchema;
export type UpdateReagentRecipeInput = z.infer<typeof updateReagentRecipeSchema>;

export const reagentRecipeQuerySchema = paginationQuerySchema.extend({
  item_id: idParamSchema.optional(),
  test_id: idParamSchema.optional(),
  parameter_id: idParamSchema.optional(),
});
export type ReagentRecipeQuery = z.infer<typeof reagentRecipeQuerySchema>;

/* ------------------------------------------------------------------------- *
 * Alerts
 * ------------------------------------------------------------------------- */

export const expiringQuerySchema = z.object({
  days: z.coerce.number().int().min(0).max(3650).default(60),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type ExpiringQuery = z.infer<typeof expiringQuerySchema>;

export const lowStockQuerySchema = z.object({
  department_id: idParamSchema.optional(),
  limit: z.coerce.number().int().min(1).max(50).default(10),
});
export type LowStockQuery = z.infer<typeof lowStockQuerySchema>;
